import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { WalletIcon } from "lucide-react";
import { useWalletAccountsQuery, useWalletSummaryQuery } from "../api/queries";
import { WalletAccountsFilter } from "../ui/WalletAccountsFilter";
import { WalletAccountsTable } from "../ui/WalletAccountsTable";
import { WalletSummaryCards } from "../ui/WalletSummaryCards";

type WalletAccountsSearch = {
  status?: string;
  search?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function WalletAccountsPage() {
  const nav = useNavigate({ from: "/app/wallet/accounts" });
  const search = useSearch({
    from: "/app/wallet/accounts",
  }) as WalletAccountsSearch;

  const params = {
    status: search.status,
    search: search.search,
    sortField: (search.sortField ?? "createdAt") as
      | "balanceLak"
      | "createdAt"
      | "customerName",
    sortOrder: (search.sortOrder ?? "desc") as "asc" | "desc",
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = useWalletAccountsQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    useWalletSummaryQuery();
  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ບັນຊີ Wallet",
              to: "/app/wallet/accounts",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <WalletIcon className="size-5" />
              ບັນຊີ Wallet
            </h2>
            <p className="text-muted-foreground">
              ດູລາຍການບັນຊີ wallet ທັງໝົດ; ເບິ່ງ Ledger ຫຼື ຕັ້ງວົງເງິນຕາມບັນຊີ.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <WalletSummaryCards
            totalActiveWallet={summaryData?.totalActiveWallet ?? 0}
            totalBalance={summaryData?.totalBalance ?? 0}
            suspendedCount={summaryData?.suspendedCount ?? 0}
            newToday={summaryData?.newToday ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <WalletAccountsFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <WalletAccountsTable
              data={items}
              isLoading={isLoading}
              offset={params.offset}
              limit={params.limit}
              totalCount={totalCount}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onViewLedger={(walletId) =>
                nav({
                  to: "/app/wallet/ledger",
                  search: { walletId },
                })
              }
              onSetLimits={(walletId) =>
                nav({
                  to: "/app/wallet/limits/$walletId",
                  params: { walletId },
                })
              }
            />
          )}
        </div>
      </Main>
    </>
  );
}
